import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Outlet } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Plus, Search, Settings, Loader2, Trash2, Menu, X, Send, RefreshCw, Pin, PinOff, Bell, BellOff, Archive, ArchiveRestore, ArrowLeft, CircleDot } from 'lucide-react';
import { GlobalMessageSearch } from '@/components/GlobalMessageSearch';
import { StatusDialog } from '@/components/StatusDialog';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';

interface Chat {
  id: string;
  is_group: boolean;
  name?: string;
  created_at: string;
  is_pinned: boolean;
  last_message?: {
    id: string;
    text: string;
    translated_texts?: Record<string, string> | null;
    sender_id: string;
    sender_language?: string | null;
    created_at: string;
  };
  other_participant?: {
    id: string;
    name: string;
    profile_image?: string;
    is_online: boolean;
  };
  unread_count: number;
  is_muted: boolean;
  is_archived: boolean;
}

interface PendingInvitation {
  id: string;
  invited_phone: string;
  created_at: string;
}

interface RealtimeMessagePayload {
  id: string;
  chat_id: string;
  sender_id: string;
  original_text: string;
  translated_texts?: Record<string, string> | null;
  sender_language?: string | null;
  created_at: string;
}

interface DesktopChatLayoutProps {
  children?: React.ReactNode;
}

export const DesktopChatLayout = ({ children }: DesktopChatLayoutProps) => {
  const isMobile = useIsMobile();
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  
  const [chats, setChats] = useState<Chat[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  // Never leave the chat list spinning forever if a request stalls.
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(t);
  }, [loading]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [chatToDelete, setChatToDelete] = useState<Chat | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<PendingInvitation | null>(null);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const chatsFetchRequestRef = useRef(0);
  const chatsRef = useRef<Chat[]>([]);
  const chatIdRef = useRef(chatId);
  const profileRef = useRef(profile);

  // Check if we're on tablet (md but not lg)
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkTablet = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width < 1024);
    };
    checkTablet();
    window.addEventListener('resize', checkTablet);
    return () => window.removeEventListener('resize', checkTablet);
  }, []);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    if (!user || typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    let initialLoadTriggered = false;
    const loadInitialData = () => {
      if (initialLoadTriggered) return;
      initialLoadTriggered = true;
      void fetchChats();
      void fetchPendingInvitations();
    };

    const resyncData = () => {
      void fetchChats();
      void fetchPendingInvitations();
    };

    const channel = supabase
      .channel(`desktop-chat-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          applyIncomingMessage(payload.new as RealtimeMessagePayload);
          void fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
        },
        () => {
          void fetchChats();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invitations',
        },
        () => {
          void fetchPendingInvitations();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          loadInitialData();
          return;
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          resyncData();
        }
      });

    const fallbackLoadTimer = setTimeout(loadInitialData, 1500);
    const periodicSyncTimer = window.setInterval(() => {
      void fetchChats();
    }, 6000);

    const handleFocus = () => {
      resyncData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resyncData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let appStateListener: { remove: () => Promise<void> } | null = null;
    let effectActive = true;

    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          resyncData();
        }
      }).then((listener) => {
        if (effectActive) {
          appStateListener = listener;
          return;
        }

        void listener.remove();
      });
    }

    return () => {
      effectActive = false;
      clearTimeout(fallbackLoadTimer);
      clearInterval(periodicSyncTimer);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (appStateListener) {
        void appStateListener.remove();
      }

      supabase.removeChannel(channel);
    };
  }, [user, navigate]);

  useEffect(() => {
    if (isMobile || isTablet || loading || chatId || chats.length === 0) return;
    // Only auto-redirect to first chat from the chat list root, not from
    // other routes (e.g. /settings, /add-contact, /create-group).
    if (window.location.pathname !== '/chats') return;
    navigate(`/chat/${chats[0].id}`, { replace: true });
  }, [isMobile, isTablet, loading, chatId, chats, navigate]);

  const notifyNewMessage = (message: RealtimeMessagePayload, chat: Chat) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (!user || message.sender_id === user.id || chat.is_muted) return;

    // Don't interrupt if this exact chat is already open and the tab is focused.
    if (document.visibilityState === 'visible' && chatIdRef.current === message.chat_id) return;

    const senderName = chat.is_group ? chat.name || 'Group chat' : chat.other_participant?.name || 'New message';
    const preferredLanguage = profileRef.current?.preferred_language || language || 'en';
    const bodyText = message.translated_texts?.[preferredLanguage] || message.original_text || '';
    const body = bodyText.length > 140 ? `${bodyText.slice(0, 140)}…` : bodyText;

    const notification = new Notification(senderName, {
      body,
      icon: (!chat.is_group && chat.other_participant?.profile_image) || '/favicon.png',
      tag: `chat-${message.chat_id}`,
    });

    notification.onclick = () => {
      window.focus();
      navigate(`/chat/${message.chat_id}`);
      notification.close();
    };
  };

  const applyIncomingMessage = (message: RealtimeMessagePayload) => {
    if (!user) return;

    const targetChatForNotification = chatsRef.current.find((chat) => chat.id === message.chat_id);
    if (targetChatForNotification) {
      notifyNewMessage(message, targetChatForNotification);
    }

    setChats((prev) => {
      const chatIndex = prev.findIndex((chat) => chat.id === message.chat_id);

      if (chatIndex === -1) {
        void fetchChats();
        return prev;
      }

      const targetChat = prev[chatIndex];
      if (targetChat.last_message?.id === message.id) {
        return prev;
      }

      const updatedChat: Chat = {
        ...targetChat,
        last_message: {
          id: message.id,
          text: message.original_text,
          translated_texts: message.translated_texts,
          sender_id: message.sender_id,
          sender_language: message.sender_language,
          created_at: message.created_at,
        },
        unread_count:
          targetChat.unread_count +
          (message.sender_id === user.id || targetChat.is_muted ? 0 : 1),
      };

      const updatedChats = [...prev];
      updatedChats[chatIndex] = updatedChat;
      updatedChats.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      return updatedChats;
    });
  };

  const fetchChats = async () => {
    if (!user) return;
    const requestId = ++chatsFetchRequestRef.current;

    try {
      const { data: participantData, error: participantError } = await supabase
        .from('chat_participants')
        .select(`
          chat_id,
          is_pinned,
          is_muted,
          is_archived,
          chats (
            id,
            is_group,
            name,
            created_at
          )
        `)
        .eq('user_id', user.id);

      if (participantError) throw participantError;

      const chatsWithMessages = await Promise.all(
        participantData.map(async (p) => {
          const chat = p.chats;
          
          const { data: messageData } = await supabase
            .from('messages')
            .select('id, original_text, translated_texts, sender_language, created_at, sender_id')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          const { count: unreadCount } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('chat_id', chat.id)
            .eq('is_read', false)
            .neq('sender_id', user.id);

          let otherParticipant = null;
          if (!chat.is_group) {
            const { data: otherParticipantData } = await supabase
              .from('chat_participants')
              .select('user_id')
              .eq('chat_id', chat.id)
              .neq('user_id', user.id)
              .maybeSingle();

            if (otherParticipantData) {
              const { data: profileData } = await supabase
                .rpc('get_chat_participant_profile', { _profile_id: otherParticipantData.user_id });
              
              if (profileData && profileData.length > 0) {
                otherParticipant = profileData[0];
              }
            }
          }

          return {
            ...chat,
            is_pinned: p.is_pinned || false,
            is_muted: p.is_muted || false,
            is_archived: p.is_archived || false,
            last_message: messageData
              ? {
                  id: messageData.id,
                  text: messageData.original_text,
                  translated_texts: messageData.translated_texts as Record<string, string> | null,
                  sender_id: messageData.sender_id,
                  sender_language: messageData.sender_language,
                  created_at: messageData.created_at,
                }
              : undefined,
            other_participant: otherParticipant,
            unread_count: unreadCount || 0,
          };
        })
      );

      if (requestId !== chatsFetchRequestRef.current) return;

      chatsWithMessages.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        const aTime = a.last_message?.created_at || a.created_at;
        const bTime = b.last_message?.created_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      setChats(chatsWithMessages);

      // Background-translate any previews that aren't yet in the user's language.
      void translateMissingPreviews(chatsWithMessages);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      if (requestId === chatsFetchRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const translateMissingPreviews = async (chatList: Chat[]) => {
    if (!user) return;
    const preferredLang = language;
    if (!preferredLang) return;

    const toTranslate = chatList.filter((c) => {
      const msg = c.last_message;
      return (
        msg &&
        msg.sender_id !== user.id &&
        msg.sender_language &&
        msg.sender_language !== preferredLang &&
        !(msg.translated_texts?.[preferredLang])
      );
    });

    if (toTranslate.length === 0) return;

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;

    await Promise.allSettled(
      toTranslate.map(async (chat) => {
        const msg = chat.last_message!;
        try {
          const { data, error } = await supabase.functions.invoke('translate-message', {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
            body: {
              text: msg.text,
              sourceLanguage: msg.sender_language,
              targetLanguages: [preferredLang],
            },
          });

          const translated = data?.translations?.[preferredLang];
          if (error || !translated) return;

          const nextTranslatedTexts = {
            ...(msg.translated_texts || {}),
            [preferredLang]: translated,
          };

          setChats((prev) =>
            prev.map((c) =>
              c.id === chat.id
                ? { ...c, last_message: { ...c.last_message!, translated_texts: nextTranslatedTexts } }
                : c
            )
          );

          void supabase
            .from('messages')
            .update({ translated_texts: nextTranslatedTexts })
            .eq('id', msg.id);
        } catch (err) {
          console.error('[DesktopChatLayout] Failed to translate preview for chat', chat.id, err);
        }
      })
    );
  };

  const fetchPendingInvitations = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('invitations')
        .select('id, invited_phone, created_at')
        .eq('inviter_id', user.id)
        .eq('processed', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingInvitations(data || []);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  // Returns the translated preview in the user's language, or original as fallback.
  const getPreviewText = (lastMessage: Chat['last_message']): string => {
    if (!lastMessage) return '';
    const tt = lastMessage.translated_texts;
    if (tt && typeof tt === 'object' && tt[language]) {
      return tt[language];
    }
    return lastMessage.text;
  };

  const archivedCount = chats.filter(c => c.is_archived).length;
  const archivedUnreadCount = chats
    .filter(c => c.is_archived && !c.is_muted)
    .reduce((sum, c) => sum + (c.unread_count || 0), 0);

  const filteredChats = chats.filter((chat) => {
    if (showArchived ? !chat.is_archived : chat.is_archived) return false;
    const searchLower = searchQuery.toLowerCase();
    if (chat.is_group) {
      return chat.name?.toLowerCase().includes(searchLower);
    }
    return chat.other_participant?.name.toLowerCase().includes(searchLower);
  });

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    setDeletingChatId(chatToDelete.id);
    try {
      const { error } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatToDelete.id);

      if (error) throw error;

      toast({
        title: t('deleteChat'),
        description: t('success'),
      });

      setChats(chats.filter(c => c.id !== chatToDelete.id));
      
      if (chatId === chatToDelete.id) {
        navigate('/chats');
      }
    } catch (error: any) {
      console.error('Error deleting chat:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setDeletingChatId(null);
      setChatToDelete(null);
    }
  };

  const handleResendInvite = async (invitation: PendingInvitation) => {
    if (!user || resendingInviteId) return;
    setResendingInviteId(invitation.id);
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', user.id)
        .single();

      const { error } = await supabase.functions.invoke('send-sms-invite', {
        body: {
          phoneNumber: invitation.invited_phone,
          inviterName: profileData?.name || 'A friend',
        },
      });

      if (error) throw error;

      toast({
        title: t('inviteResent'),
        description: t('inviteSent'),
      });
    } catch (error) {
      console.error('Error resending invite:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleCancelInvite = async () => {
    if (!inviteToCancel) return;
    setCancellingInviteId(inviteToCancel.id);
    try {
      const { error } = await supabase
        .from('invitations')
        .delete()
        .eq('id', inviteToCancel.id);

      if (error) throw error;

      setPendingInvitations(prev => prev.filter(i => i.id !== inviteToCancel.id));
      toast({
        title: t('inviteCancelled'),
      });
    } catch (error) {
      console.error('Error cancelling invite:', error);
      toast({
        title: t('error'),
        description: t('tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setCancellingInviteId(null);
      setInviteToCancel(null);
    }
  };

  const togglePinChat = async (chatId: string, currentPinned: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_pinned: !currentPinned })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev => {
        const updated = prev.map(c => c.id === chatId ? { ...c, is_pinned: !currentPinned } : c);
        updated.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          const aTime = a.last_message?.created_at || a.created_at;
          const bTime = b.last_message?.created_at || b.created_at;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
        return updated;
      });
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const toggleMuteChat = async (chatId: string, currentMuted: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_muted: !currentMuted })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev =>
        prev.map(c => c.id === chatId ? { ...c, is_muted: !currentMuted } : c)
      );
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const toggleArchiveChat = async (chatId: string, currentArchived: boolean) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('chat_participants')
        .update({ is_archived: !currentArchived })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;
      setChats(prev =>
        prev.map(c => c.id === chatId ? { ...c, is_archived: !currentArchived } : c)
      );
      toast({
        title: !currentArchived ? t('archiveChat') : t('unarchiveChat'),
      });
    } catch (error) {
      console.error('Error toggling archive:', error);
    }
  };

  // On mobile, just render children (original behavior)
  if (isMobile) {
    return <>{children}</>;
  }

  // Sidebar content component (shared between sheet and static sidebar)
  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {showArchived && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowArchived(false)}
              title={t('hideArchived')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <Avatar className="h-10 w-10">
            <AvatarImage src={profile?.profile_image} />
            <AvatarFallback>{profile?.name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="font-semibold">
              {showArchived ? t('archivedChats') : profile?.name}
            </h1>
            <p className="text-xs text-muted-foreground">Global Chat Connect</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {!showArchived && archivedCount > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowArchived(true)}
              title={t('showArchived')}
              className="relative"
            >
              <Archive className="h-5 w-5" />
              {archivedUnreadCount > 0 && (
                <Badge
                  variant="default"
                  className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px]"
                >
                  {archivedUnreadCount}
                </Badge>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowStatus(true)}
            title="Status"
          >
            <CircleDot className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-5 w-5" />
          </Button>
          {isTablet && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="p-4 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <GlobalMessageSearch searchQuery={searchQuery} onResultClick={() => { if (isTablet) setSidebarOpen(false); }} />
      </div>

      {/* Chat list */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredChats.length === 0 && (showArchived || pendingInvitations.length === 0) ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">
              {showArchived ? t('noArchivedChats') : t('noChats')}
            </h3>
            {!showArchived && (
              <>
                <p className="text-muted-foreground mb-4 text-sm">
                  {t('startChatting')}
                </p>
                <Button size="sm" onClick={() => {
                  navigate('/add-contact');
                  setSidebarOpen(false);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addContact')}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                className={`group flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                  chatId === chat.id ? 'bg-muted' : ''
                }`}
                onClick={() => {
                  navigate(`/chat/${chat.id}`);
                  setSidebarOpen(false);
                }}
              >
                <Avatar className="h-12 w-12 flex-shrink-0">
                  <AvatarImage src={chat.other_participant?.profile_image} />
                  <AvatarFallback>
                    {chat.is_group
                      ? chat.name?.[0]
                      : chat.other_participant?.name[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  {/* Row 1: name (left) + timestamp (right) */}
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {chat.is_pinned && <Pin className="h-3 w-3 text-primary flex-shrink-0" />}
                      {chat.is_muted && <BellOff className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                      <h3 className={`truncate ${!chat.is_muted && chat.unread_count > 0 ? "font-bold" : "font-semibold"}`}>
                        {chat.is_group ? chat.name : chat.other_participant?.name}
                      </h3>
                    </div>
                    {chat.last_message && (
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDistanceToNow(new Date(chat.last_message.created_at), {
                          addSuffix: false,
                        })}
                      </span>
                    )}
                  </div>
                  {/* Row 2: preview (left) + unread badge (right) */}
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm truncate flex-1 min-w-0 ${!chat.is_muted && chat.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                      {chat.last_message ? getPreviewText(chat.last_message) : t('noChats')}
                    </p>
                    {chat.unread_count > 0 && (
                      <Badge
                        variant="default"
                        className={`h-5 min-w-5 px-1.5 text-xs flex-shrink-0 ${chat.is_muted ? 'opacity-50' : ''}`}
                      >
                        {chat.unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
                {!chat.is_group && chat.other_participant?.is_online && (
                  <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-primary flex-shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePinChat(chat.id, chat.is_pinned);
                  }}
                  title={chat.is_pinned ? t('unpinChat') : t('pinChat')}
                >
                  {chat.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-primary flex-shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMuteChat(chat.id, chat.is_muted);
                  }}
                  title={chat.is_muted ? t('unmuteChat') : t('muteChat')}
                >
                  {chat.is_muted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-primary flex-shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleArchiveChat(chat.id, chat.is_archived);
                  }}
                  title={chat.is_archived ? t('unarchiveChat') : t('archiveChat')}
                >
                  {chat.is_archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setChatToDelete(chat);
                  }}
                  disabled={deletingChatId === chat.id}
                >
                  {deletingChatId === chat.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
            {!showArchived && pendingInvitations.map((invitation) => (
              <div
                key={`inv-${invitation.id}`}
                className="flex items-center gap-3 px-4 py-3 opacity-50"
              >
                <Avatar className="h-12 w-12 flex-shrink-0">
                  <AvatarFallback className="bg-muted text-muted-foreground">
                    <Send className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-muted-foreground truncate">{invitation.invited_phone}</h3>
                    <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                      {formatDistanceToNow(new Date(invitation.created_at), {
                        addSuffix: false,
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground italic">
                    {t('invitationPending')}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary flex-shrink-0"
                  onClick={() => handleResendInvite(invitation)}
                  disabled={resendingInviteId === invitation.id}
                >
                  {resendingInviteId === invitation.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  {resendingInviteId === invitation.id ? t('resending') : t('resendInvite')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                  onClick={() => setInviteToCancel(invitation)}
                  disabled={cancellingInviteId === invitation.id}
                >
                  {cancellingInviteId === invitation.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Action buttons */}
      <div className="p-4 border-t flex gap-2 flex-shrink-0">
        <Button
          className="flex-1"
          variant="secondary"
          onClick={() => {
            navigate('/create-group');
            setSidebarOpen(false);
          }}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          {t('createGroup')}
        </Button>
        <Button
          className="flex-1"
          onClick={() => {
            navigate('/add-contact');
            setSidebarOpen(false);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('addContact')}
        </Button>
      </div>
    </>
  );

  // Tablet: Sheet-based sidebar
  if (isTablet) {
    return (
      <div className="safe-area-screen flex flex-col bg-background overflow-hidden">
        {/* Top header with menu button */}
        <div className="border-b bg-card p-3 flex items-center gap-3 flex-shrink-0">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0 flex flex-col">
              <SidebarContent />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2 flex-1">
            <Avatar className="h-8 w-8">
              <AvatarImage src={profile?.profile_image} />
              <AvatarFallback>{profile?.name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="font-semibold truncate">{profile?.name}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowStatus(true)}
            title="Status"
          >
            <CircleDot className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <Settings className="h-5 w-5" />
          </Button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-h-0">
          {children || (
            <div className="flex-1 flex items-center justify-center bg-muted/20">
              <div className="text-center px-4">
                <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">{t('noChats')}</h2>
                <p className="text-muted-foreground mb-4">{t('startChatting')}</p>
                <Button onClick={() => setSidebarOpen(true)}>
                  <Menu className="h-4 w-4 mr-2" />
                  {t('chats')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!chatToDelete} onOpenChange={() => setChatToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteChat')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deleteChatConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteChat}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!inviteToCancel} onOpenChange={() => setInviteToCancel(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('cancelInvite')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('cancelInviteConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelInvite}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Desktop (lg+): split-panel layout
  return (
    <div className="safe-area-screen flex bg-background overflow-hidden">
      {/* Left sidebar - Chat list */}
      <div className="w-80 lg:w-96 border-r flex flex-col bg-card flex-shrink-0">
        <SidebarContent />
      </div>

      {/* Right panel - Chat content or placeholder */}
      <div className="flex-1 flex flex-col min-w-0">
        {children || (
          <div className="flex-1 flex items-center justify-center bg-muted/20">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">{t('noChats')}</h2>
              <p className="text-muted-foreground">{t('startChatting')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!chatToDelete} onOpenChange={() => setChatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteChat')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteChatConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChat}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!inviteToCancel} onOpenChange={() => setInviteToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cancelInvite')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cancelInviteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInvite}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <StatusDialog open={showStatus} onOpenChange={setShowStatus} />
    </div>
  );
};

export default DesktopChatLayout;
